import { useEffect, useMemo, useState } from 'react';
import { CardFace } from './CardOverlay';
import RowCard from './RowCard';
import CollapsedCard from './CollpasedCard';
import { DeckTop } from './DeckWithHover';
import { SpecialTechTile, ColonyPill } from './SideTiles';
import TopBar from './TopBar';
import { SetTitle } from '../utils/Lobby';
import './Lobby.css';
const { DeckDataFactory } = require('@tta/engine/Factories/DeckDataFactory');
const CardConverter = require('@tta/engine/Utilities/Converters/CardConverter');
const { Age, CardDeck } = require('@tta/engine/Constants/Constants');
const { Setups } = require('@tta/engine/Constants/CardVersions');

// The cards page (open the app with ?cards): every card once, grouped by type and then by age, drawn as it is on hover
// (in full); filtered by type, age, set (base game or expansion) and name. "On the board" adds each card's small forms
// as the game draws them (a civil card as in the card row, a military card as in hand, an event as it lies on top of a
// deck, a special technology or a territory as in a civilization's side boxes), which also makes this the page for
// checking how cards look. A leader or wonder the expansion changed is one card with a switch between its two versions
// (its expansion version first); picking the base game or the expansion shows only that version.
const Ages = [Age.AgeA, Age.Age1, Age.Age2, Age.Age3];
const AgeLabels = { [Age.AgeA]: 'A', [Age.Age1]: 'I', [Age.Age2]: 'II', [Age.Age3]: 'III' };
const Types = [
    ['Leader', 'Leaders'], ['Wonder', 'Wonders'], ['Technology', 'Technologies'], ['Action', 'Actions'],
    ['Event', 'Events'], ['Territory', 'Territories'], ['Aggression', 'Aggressions'], ['War', 'Wars'], ['Pact', 'Pacts'],
    ['Tactic', 'Tactics'], ['Bonus', 'Bonuses'],
];
const TechnologyTypes = require('@tta/engine/Constants/TechnologyTypes');
const SpecialTypes = Object.values(TechnologyTypes.Special);
// Technologies by colour, as on a civilization's board: production (brown), urban (grey), military (red), special
// (blue), then governments (orange)
const TechnologyGroups = [TechnologyTypes.Production, TechnologyTypes.Urban, TechnologyTypes.Military, TechnologyTypes.Special]
    .map(group => Object.values(group));
const technologyGroup = (card) => {
    const index = TechnologyGroups.findIndex(group => group.includes(card.Technology?.type));
    return index < 0 ? TechnologyGroups.length : index;
};
// Not in any deck: the cards everyone starts with
const Starting = ['Agriculture', 'Bronze', 'Philosophy', 'Religion', 'Warrior', 'Despotism']
    .map(name => ({ type: 'Technology', name, age: Age.AgeA }));

// Every card, as [{ card, set: 'both' | 'base' | 'expansion' }]: in the base game only (a base version the expansion
// changed), the expansion only (a changed version, or a card new to it), or both
const AllCards = () => {
    const seen = new Set();
    let id = 1;
    return Ages.flatMap(age => {
        const decks = (setup) => [...DeckDataFactory.GetCivilDeck(age, 4, setup), ...DeckDataFactory.GetMilitaryDeck(age, 4, setup)];
        const base = decks(Setups.Base);
        const inBase = new Set([...Starting, ...base].map(card => `${card.type}|${card.name}`));
        return [...(age === Age.AgeA ? Starting : []), ...decks(Setups.Expansion)]
            // (a changed card: its base version first, when the base game has it)
            .flatMap(card => card.version ? [base.find(other => other.name === card.name && other.type === card.type), card].filter(Boolean) : [card])
            .filter(card => {
                const key = `${card.type}|${card.name}|${card.age}|${card.version ?? ''}`;
                return !seen.has(key) && seen.add(key);
            })
            .map(card => {
                let converted = null;
                try { converted = CardConverter({ ...card, id: id++ }); } catch (e) { converted = null; }
                const set = card.version === 'base' ? 'base' : card.version === 'expansion' || !inBase.has(`${card.type}|${card.name}`) ? 'expansion' : 'both';
                return converted && { card: converted, set };
            })
            .filter(Boolean);
    });
};

function CardGallery()
{
    const all = useMemo(AllCards, []);
    useEffect(() => SetTitle('Cards · Through the Ages'), []);
    const [type, setType] = useState('all');
    const [age, setAge] = useState('all');
    const [set, setSet] = useState('all');
    const [search, setSearch] = useState('');
    const [small, setSmall] = useState(false);

    const wanted = search.trim().toLowerCase();
    const matching = all.filter(entry => (age === 'all' || entry.card.age === age)
        && (set === 'all' || entry.set === 'both' || entry.set === set)
        && (!wanted || entry.card.name.toLowerCase().includes(wanted)));
    // (a card in both versions: one entry, its expansion version with its base one beside it)
    const sameCard = (a, b) => a.card.type === b.card.type && a.card.name === b.card.name && a.card.age === b.card.age;
    const shown = matching.filter(entry => entry.set !== 'base' || !matching.some(other => other.set === 'expansion' && sameCard(entry, other)))
        .map(entry => entry.set !== 'expansion' ? entry
            : { ...entry, base: matching.find(other => other.set === 'base' && sameCard(entry, other))?.card });
    const countOf = (name) => shown.filter(entry => entry.card.type === name).length;

    return <div className="tta-lobby">
        <TopBar page="cards"/>
        <div className="tta-cards-filters">
            <input className="tta-input tta-cards-search" placeholder="Search by name" value={search} onChange={(e) => setSearch(e.target.value)}/>
            <div className="tta-chips">
                <Chip on={type === 'all'} onClick={() => setType('all')}>All types <span>{shown.length}</span></Chip>
                {Types.map(([name, label]) =>
                    <Chip key={name} on={type === name} onClick={() => setType(name)} disabled={countOf(name) === 0}>{label} <span>{countOf(name)}</span></Chip>)}
            </div>
            <div className="tta-cards-filter-row">
                <span className="tta-cards-filter-label">Age</span>
                <div className="tta-segmented">
                    <button className={age === 'all' ? 'on' : ''} onClick={() => setAge('all')}>All</button>
                    {Ages.map(each => <button key={each} className={age === each ? 'on' : ''} onClick={() => setAge(each)}>{AgeLabels[each]}</button>)}
                </div>
                <span className="tta-cards-filter-label">Cards</span>
                <div className="tta-segmented">
                    <button className={set === 'all' ? 'on' : ''} onClick={() => setSet('all')}>Everything</button>
                    <button className={set === 'base' ? 'on' : ''} onClick={() => setSet('base')}>Base game</button>
                    <button className={set === 'expansion' ? 'on' : ''} onClick={() => setSet('expansion')}>With the expansion</button>
                </div>
                <label className="tta-cards-small">
                    <input type="checkbox" checked={small} onChange={(e) => setSmall(e.target.checked)}/> Show how they look on the board
                </label>
            </div>
        </div>
        <main className="tta-cards-main">
            {shown.length === 0 && <div className="tta-muted">No cards match.</div>}
            {Types.filter(([name]) => type === 'all' || type === name).map(([name, label]) => {
                const cards = shown.filter(entry => entry.card.type === name);
                if (cards.length === 0) return null;
                return <section key={name} className="tta-cards-section">
                    <h2 className="tta-cards-type">{label} <span className="tta-muted">{cards.length}</span></h2>
                    {Ages.map(each => {
                        // (by name; technologies by colour first)
                        const ofAge = cards.filter(entry => entry.card.age === each).sort((a, b) =>
                            (name === 'Technology' ? technologyGroup(a.card) - technologyGroup(b.card) : 0) || a.card.name.localeCompare(b.card.name));
                        if (ofAge.length === 0) return null;
                        return <div key={each}>
                            <div className="tta-cards-age">Age {AgeLabels[each]}</div>
                            <div className="tta-gallery-cards">
                                {ofAge.map(({ card, base, set: cardSet }) => <GalleryCard key={card.id} card={card} base={base} set={cardSet} small={small}/>)}
                            </div>
                        </div>;
                    })}
                </section>;
            })}
        </main>
    </div>;
}

const Chip = ({ on, onClick, disabled, children }) =>
    <button className={`tta-chip${on ? ' on' : ''}`} onClick={onClick} disabled={disabled}>{children}</button>;

// A card in full, with which game it belongs to (when not both), and, if asked for, its small forms. base: the base
// game's version of a card the expansion changed, switched to under it.
function GalleryCard({ card: expansion, base, set, small })
{
    const [showBase, setShowBase] = useState(false);
    const card = base && showBase ? base : expansion;
    return <div className="tta-gallery-card">
        <div>
            <CardFace card={card}/>
            {/* (the same space under every card, whatever goes in it, so they all line up) */}
            <div className="tta-gallery-foot">
                {base
                    ? <div className="tta-segmented tta-version-switch" title="This card is different in the base game and the expansion">
                        <button className={showBase ? 'on' : ''} onClick={() => setShowBase(true)}>Base</button>
                        <button className={!showBase ? 'on' : ''} onClick={() => setShowBase(false)}>Expansion</button>
                    </div>
                    : set !== 'both' && <div className={`tta-gallery-version ${set}`}>
                        {card.version ? (set === 'base' ? 'Base game version' : 'Expansion version') : 'Expansion'}</div>}
            </div>
        </div>
        {small && <div className="tta-gallery-small">
            {card.deck === CardDeck.Military ? <CollapsedCard card={card}/> : <RowCard card={card}/>}
            {card.type === 'Event' && <div className="tta-gallery-deck"><DeckTop card={card}/></div>}
            {card.Technology && SpecialTypes.includes(card.Technology.type) &&
                <div className="tta-side-box" style={{ backgroundColor: '#bdc7e2' }}><SpecialTechTile tech={card.Technology}/></div>}
            {card.type === 'Territory' &&
                <div className="tta-side-box" style={{ backgroundColor: '#cfda8b' }}><ColonyPill colony={card}/></div>}
        </div>}
    </div>;
}

export default CardGallery;
